import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Link
} from '@mui/material';
import {
  Description as FileIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://test2.planfix-to-syrve.com';

const FilesPage = () => {
  const { folderName } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  
  const [backupsDialogOpen, setBackupsDialogOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [currentBackupFile, setCurrentBackupFile] = useState(null);

  const decodedFolderName = decodeURIComponent(folderName);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/files/folder/${encodeURIComponent(decodedFolderName)}`);
      if (response.data.success) {
        setFiles(response.data.data);
      } else {
        setError('Помилка завантаження файлів');
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Помилка завантаження файлів: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [decodedFolderName]);

  const fetchBackups = useCallback(async (fileName) => {
    try {
      setLoadingBackups(true);
      const response = await axios.get(`${API_BASE_URL}/api/files/backups`, {
        params: { folderName: decodedFolderName, fileName }
      });
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError('Помилка завантаження бекапів');
    } finally {
      setLoadingBackups(false);
    }
  }, [decodedFolderName]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleBack = () => {
    navigate('/');
  };

  
  const handleViewPdf = (fileName) => {
    
    const url = `${API_BASE_URL}/api/files/view/${encodeURIComponent(decodedFolderName)}/${encodeURIComponent(fileName)}`;
    window.open(url, '_blank');
  };

  
  const handleDownload = async (fileName) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/files/download`, {
        params: {
          folderName: decodedFolderName,
          fileName: fileName
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess(`Файл ${fileName} завантажено`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Помилка завантаження файлу');
    }
  };

  const handleOpenUploadDialog = () => {
    setUploadFileName('');
    setUploadFile(null);
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = ['.pdf', '.xls', '.xlsx'];
      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (validExtensions.includes(fileExt)) {
        setUploadFile(file);
        setUploadFileName(file.name);
      } else {
        setError('Будь ласка, виберіть файл .pdf, .xls або .xlsx');
      }
    }
  };

  const handleUploadFile = async () => {
    if (!uploadFile) {
      setError('Виберіть файл для завантаження');
      return;
    }

    if (!uploadFileName) {
      setError('Вкажіть назву файлу');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('folderName', decodedFolderName);
      formData.append('fileName', uploadFileName);
      
      const response = await axios.post(`${API_BASE_URL}/api/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setSuccess(`Файл ${uploadFileName} успішно збережено`);
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadFileName('');
        fetchFiles();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка завантаження');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Помилка при завантаженні файлу');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileName) => {
    if (!window.confirm(`Ви впевнені, що хочете видалити файл ${fileName}?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/files/delete`, {
        params: {
          folderName: decodedFolderName,
          fileName: fileName
        }
      });
      
      if (response.data.success) {
        setSuccess(`Файл ${fileName} видалено`);
        fetchFiles();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Помилка видалення файлу');
    }
  };

  const handleOpenBackupsDialog = async (file) => {
    setCurrentBackupFile(file);
    await fetchBackups(file.name);
    setBackupsDialogOpen(true);
  };

  const handleRestoreBackup = async (backupName) => {
    if (!window.confirm(`Відновити файл ${currentBackupFile.name} з бекапу ${backupName}?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/files/restore-backup`, {
        folderName: decodedFolderName,
        fileName: currentBackupFile.name,
        backupName: backupName
      });
      
      if (response.data.success) {
        setSuccess(`Файл ${currentBackupFile.name} відновлено`);
        setBackupsDialogOpen(false);
        fetchFiles();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error restoring backup:', err);
      setError('Помилка відновлення');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date) => {
    if (!date) return 'Невідомо';
    return new Date(date).toLocaleString('uk-UA');
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') {
      return <PdfIcon sx={{ color: '#dc3545', fontSize: 32 }} />;
    }
    return <FileIcon sx={{ color: '#4caf50', fontSize: 32 }} />;
  };

  const isPdf = (fileName) => {
    return fileName?.toLowerCase().endsWith('.pdf');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', mt: 8 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ color: '#fff', borderColor: '#555' }}
        >
          На головну
        </Button>
        <Typography variant="h4" sx={{ color: '#fff' }}>
          {decodedFolderName}
        </Typography>
        <Chip label={`Файлів: ${files.length}`} color="primary" />
        <Button 
          variant="contained" 
          startIcon={<UploadIcon />}
          onClick={handleOpenUploadDialog}
        >
          Додати файл
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Snackbar 
        open={!!success} 
        autoHideDuration={3000} 
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : files.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="textSecondary" variant="h6">
            В папці "{decodedFolderName}" немає файлів
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<UploadIcon />}
            onClick={handleOpenUploadDialog}
            sx={{ mt: 2 }}
          >
            Додати перший файл
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {files.map((file) => (
            <Grid item xs={12} sm={6} md={4} key={file.name}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                backgroundColor: '#2a2a3e',
                color: '#fff'
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {getFileIcon(file.name)}
                    <Chip 
                      label={file.name?.split('.').pop()?.toUpperCase() || 'FILE'} 
                      size="small" 
                      color="info" 
                    />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      wordBreak: 'break-word', 
                      fontWeight: 'bold', 
                      color: '#fff',
                      cursor: isPdf(file.name) ? 'pointer' : 'default',
                      '&:hover': isPdf(file.name) ? { color: '#4caf50' } : {}
                    }}
                    onClick={() => isPdf(file.name) && handleViewPdf(file.name)}
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatFileSize(file.size)} • {formatDate(file.modified)}
                  </Typography>
                </CardContent>
                <CardActions>
                  {}
                  {isPdf(file.name) && (
                    <Button 
                      size="small" 
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleViewPdf(file.name)}
                      sx={{ color: '#17a2b8' }}
                    >
                      Відкрити
                    </Button>
                  )}
                  
                  {}
                  <Button 
                    size="small" 
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(file.name)}
                    sx={{ color: '#4caf50' }}
                  >
                    Завантажити
                  </Button>
                  
                  <IconButton 
                    size="small" 
                    title="Замінити файл"
                    onClick={() => {
                      setUploadFileName(file.name);
                      setUploadFile(null);
                      setUploadDialogOpen(true);
                    }}
                    sx={{ color: '#ffc107' }}
                  >
                    <UploadIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    title="Історія бекапів"
                    onClick={() => handleOpenBackupsDialog(file)}
                    sx={{ color: '#17a2b8' }}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    title="Видалити"
                    onClick={() => handleDeleteFile(file.name)}
                    sx={{ color: '#dc3545' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {uploadFileName && files.some(f => f.name === uploadFileName) 
            ? 'Заміна файлу' 
            : 'Завантаження файлу'}
          <IconButton onClick={() => setUploadDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Папка: <strong>{decodedFolderName}</strong>
              {uploadFileName && files.some(f => f.name === uploadFileName) && (
                <><br />Замінюємо: <strong>{uploadFileName}</strong></>
              )}
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Назва файлу:
              </Typography>
              <input
                type="text"
                value={uploadFileName}
                onChange={(e) => setUploadFileName(e.target.value)}
                placeholder="Введіть назву файлу"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: '#fff'
                }}
              />
            </Box>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 1 }}
              startIcon={<UploadIcon />}
            >
              {uploadFile ? uploadFile.name : 'Вибрати файл (.pdf, .xls, .xlsx)'}
              <input
                type="file"
                hidden
                accept=".pdf,.xls,.xlsx"
                onChange={handleFileSelect}
              />
            </Button>

            {uploadFile && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                Вибрано: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Скасувати
          </Button>
          <Button 
            onClick={handleUploadFile} 
            variant="contained" 
            color={uploadFileName && files.some(f => f.name === uploadFileName) ? 'warning' : 'primary'}
            disabled={!uploadFile || !uploadFileName || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      {}
      <Dialog open={backupsDialogOpen} onClose={() => setBackupsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Резервні копії: {currentBackupFile?.name}
          <IconButton onClick={() => setBackupsDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingBackups ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={30} />
            </Box>
          ) : backups.length === 0 ? (
            <Typography textAlign="center" py={4} color="textSecondary">
              Немає резервних копій для цього файлу
            </Typography>
          ) : (
            <List>
              {backups.map((backup, idx) => (
                <React.Fragment key={backup.name + idx}>
                  <ListItem>
                    <ListItemText
                      primary={backup.name}
                      secondary={`Дата: ${formatDate(backup.date)} • Розмір: ${formatFileSize(backup.size)}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => handleRestoreBackup(backup.name)}
                      >
                        Відновити
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < backups.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupsDialogOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FilesPage;
